import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, ButtonGroup, makeStyles, Theme } from '@material-ui/core';
import clsx from 'clsx';
import { FontIcon } from '@moderepo/mode-app-components';
import { useSelectDevice, useSelectHome, useSelectHomeMembers, useSelectProject, useSelectUser } from '../../utils/dataStateHooks';
import { useRitzTranslation } from '../../utils/customHooks';
import { RouteHelper } from '../routes/RouteHelper';


export const usePageInfoStyles = makeStyles((theme: Theme) => {
    return {
        root: {
            display        : 'none',
            flexDirection  : 'row',
            flexWrap       : 'wrap',
            alignItems     : 'center',
            justifyContent : 'flex-start',
            padding        : theme.spacing(1),
            '&:not(:empty)': {
                display: 'flex',
            },
        },

        infoItem: {
            display       : 'flex',
            flexDirection : 'row',
            flexWrap      : 'wrap',
            alignItems    : 'center',
            justifyContent: 'flex-start',
            marginRight   : theme.spacing(2),
            '&:last-child': {
                '& $infoItemIcon': {
                    color: theme.palette.primary.light,
                },
                '& $infoItemName': {
                    color: theme.palette.primary.main,
                },
                '& $infoItemId': {
                    color: theme.palette.primary.light,
                },
            },
        },

        infoItemIconButton: {
            padding: 0,
        },

        infoItemIcon: {
            fontSize   : 20,
            width      : 20,
            height     : 20,
            marginRight: theme.spacing(0.5),
            color      : theme.palette.text.secondary,
        },

        infoItemLabel: {
            textTransform: 'none',
            display      : 'flex',
            flexDirection: 'row',
            alignItems   : 'flex-start',
        },

        infoItemName: {
            textTransform: 'none',
            color        : theme.palette.text.primary,
            lineHeight   : '1em',
            fontWeight   : 'bold',
            marginRight  : theme.spacing(0.5),
        },

        infoItemId: {
            textTransform: 'none',
            color        : theme.palette.text.secondary,
            lineHeight   : '1em',
        },

        infoItemLoading: {
            marginLeft: theme.spacing(0.5),
        },
    };
}, { name: 'PageInfoBarContainer', index: 1 });



export interface PageInfoItemUserProps {
    readonly projectId: number;
    readonly userId: number | undefined;
    readonly homeId?: number | undefined;
    readonly deviceId?: number | undefined;
    readonly icon?: string | undefined;
}


/**
 * Component for displaying User info
 */
export const PageInfoItemUser: React.FC<PageInfoItemUserProps> = (props: PageInfoItemUserProps) => {
    const navigate = useNavigate();
    const project = useSelectProject(props.projectId);

    // If userId is not provided and homeId is not provided but deviceId is provided then we will try to fetch deviceData. From device data,
    // we can get the home and then the user but it will require additional data so only do this if userId and homeId is not provided
    const device = useSelectDevice(props.projectId, !props.userId && !props.homeId && props.deviceId ? props.deviceId : undefined);

    // Decide which homeId we will use. If homeId is not provided but we have a device object and it is linked to a home then use the
    // homeId from the device. Otherwise, use the provided homeId
    const resolvedHomeId = !props.homeId && device && device.homeId ? device.homeId : props.homeId;

    // If userId is not provided but homeId is provided then we will try to fetch home members for the given homeId and then use
    // one of the home members as user. This will require loading extra data so only do this if userId is not provided
    const homeMembers = useSelectHomeMembers(!props.userId && resolvedHomeId ? resolvedHomeId : undefined);

    // Decide which userId we will use. If userId is not provided then we will try to use the userId from one of the homeMember
    const resolvedUserId = !props.userId && homeMembers && homeMembers.length > 0 ? homeMembers[0].userId : props.userId;

    // Once we have the userId, we can now fetch userInfo
    const user = useSelectUser(props.projectId, resolvedUserId);

    const { trans } = useRitzTranslation(project);
    const classes = usePageInfoStyles();
    const routeHelper = RouteHelper.getInstance(navigate);

    const onItemListingClick = useCallback(() => {
        routeHelper.gotoUsersPage(props.projectId);
    }, [props.projectId, routeHelper]);

    const onItemInstanceClick = useCallback(() => {
        if (!user) {
            return;
        }
        routeHelper.gotoUserPage({
            projectId: props.projectId,
            userId   : user.id,
        });
    }, [props.projectId, routeHelper, user]);

    // There is a chance there is no user object when none of the props is provided therefore we need to return empty element
    if (!user) {
        return (
            <></>
        );
    }

    return (
        <ButtonGroup
            variant="text"
            className={clsx(classes.infoItem)}
        >
            <Button className={classes.infoItemIconButton} onClick={onItemListingClick}>
                <FontIcon className={classes.infoItemIcon} iconName={props.icon || trans('icons.user')} />
            </Button>
            <Button
                onClick={onItemInstanceClick}
            >
                <div className={classes.infoItemLabel}>
                    {(user.name || user.email || user.phoneNumber) && (
                        <div className={classes.infoItemName}>
                            {user.name || user.email || user.phoneNumber}
                        </div>
                    )}
                    <div className={classes.infoItemId}>
                        #{user.id}
                    </div>
                </div>
            </Button>
        </ButtonGroup>
    );
};



export interface PageInfoItemHomeProps {
    readonly projectId: number;
    readonly userId?: number | undefined;
    readonly homeId: number | undefined;
    readonly deviceId?: number | undefined;
    readonly icon?: string | undefined;
}


/**
 * Component for displaying Home info
 */
export const PageInfoItemHome: React.FC<PageInfoItemHomeProps> = (props: PageInfoItemHomeProps) => {
    const navigate = useNavigate();

    const project = useSelectProject(props.projectId);

    // If homeId is not provided but deviceId is provided then we will try to fetch deviceData. From device data,
    // we can get the home but it will require additional data so only do this if homeId is not provided
    const device = useSelectDevice(props.projectId, !props.homeId && props.deviceId ? props.deviceId : undefined);

    // Decide which homeId we will use. If homeId is not provided but we have a device object and it is linked to a home then use the
    // homeId from the device. Otherwise, use the provided homeId
    const resolvedHomeId = !props.homeId && device && device.homeId ? device.homeId : props.homeId;

    // Once we have the homeId, we can now fetch home info
    const home = useSelectHome(resolvedHomeId);

    const { trans } = useRitzTranslation(project);
    const classes = usePageInfoStyles();
    const routeHelper = RouteHelper.getInstance(navigate);

    const onItemListingClick = useCallback(() => {
        routeHelper.gotoHomesPage(props.projectId);
    }, [props.projectId, routeHelper]);

    const onItemInstanceClick = useCallback(() => {
        if (!home) {
            return;
        }
        routeHelper.gotoHomeDetailsPage({
            projectId: props.projectId,
            userId   : props.userId,
            homeId   : home.id,
        });
    }, [home, props.projectId, props.userId, routeHelper]);


    // There is a chance there is no home object when none of the props is provided therefore we need to return empty element
    if (!home) {
        return (
            <></>
        );
    }

    return (
        <ButtonGroup
            variant="text"
            className={clsx(classes.infoItem)}
        >
            <Button className={classes.infoItemIconButton} onClick={onItemListingClick}>
                <FontIcon className={classes.infoItemIcon} iconName={props.icon || trans('icons.home')} />
            </Button>
            <Button
                onClick={onItemInstanceClick}
            >
                <div className={classes.infoItemLabel}>
                    {home.name && (
                        <div className={classes.infoItemName}>
                            {home.name}
                        </div>
                    )}
                    <div className={classes.infoItemId}>
                        #{home.id}
                    </div>
                </div>
            </Button>
        </ButtonGroup>
    );
};



export interface PageInfoItemDeviceProps {
    readonly projectId: number;
    readonly userId?: number | undefined;
    readonly homeId?: number | undefined;
    readonly deviceId: number | undefined;
    readonly icon?: string | undefined;
}

/**
 * Component for displaying Robot info
 */
export const PageInfoItemDevice: React.FC<PageInfoItemDeviceProps> = (props: PageInfoItemDeviceProps) => {
    const navigate = useNavigate();

    const project = useSelectProject(props.projectId);
    const device = useSelectDevice(props.projectId, props.deviceId);

    const { trans } = useRitzTranslation(project);
    const classes = usePageInfoStyles();
    const routeHelper = RouteHelper.getInstance(navigate);

    const onItemListingClick = useCallback(() => {
        routeHelper.gotoProjectDevicesPage(props.projectId);
    }, [props.projectId, routeHelper]);

    const onItemInstanceClick = useCallback(() => {
        if (!device) {
            return;
        }
        routeHelper.gotoDevicePage({
            projectId: props.projectId,
            userId   : props.userId,
            homeId   : props.homeId,
            deviceId : device.id,
        });
    }, [device, props.homeId, props.projectId, props.userId, routeHelper]);

    // There is a chance there is no device object when none of the props is provided therefore we need to return empty element
    if (!device) {
        return (
            <></>
        );
    }

    return (
        <ButtonGroup
            variant="text"
            className={clsx(classes.infoItem)}
        >
            <Button className={classes.infoItemIconButton} onClick={onItemListingClick}>
                <FontIcon className={classes.infoItemIcon} iconName={props.icon || trans('icons.device')} />
            </Button>
            <Button
                onClick={onItemInstanceClick}
            >
                <div className={classes.infoItemLabel}>
                    {device.name && (
                        <div className={classes.infoItemName}>
                            {device.name}
                        </div>
                    )}
                    <div className={classes.infoItemId}>
                        #{device.id}
                    </div>
                </div>
            </Button>
        </ButtonGroup>
    );
};
